import { DatePart, DateUtil } from "./Date";
import { NullDateRangeDateBehavior } from "./NullDateRangeDateBehavior";

export interface DateRangeProps {
    startDate: string | Date;
    endDate: string | Date;
    nullStartDateBehavior?: NullDateRangeDateBehavior;
    nullEndDateBehavior?: NullDateRangeDateBehavior;
}

export class DateRange extends Date {
    private _startDate: Date;
    private _endDate: Date;
    private _nullStartDateBehavior: NullDateRangeDateBehavior;
    private _nullEndDateBehavior: NullDateRangeDateBehavior;

    constructor(props?: Partial<DateRangeProps>) {
        super();
        Object.assign(this, props);
    }

    private normalizeDate(date: string | Date): Date {
        return typeof date === "string" ? DateUtil.parseDateWithKeywords(date, true, false) : date;
    }

    get startDate(): Date {
        if (this._startDate == null) {
            if (this.nullStartDateBehavior === NullDateRangeDateBehavior.USE_OTHER_DATE
                && DateUtil.isDateValid(this._endDate)) {
                return DateUtil.setToBeginningOfDay(this._endDate);
            }
            if (this.nullStartDateBehavior === NullDateRangeDateBehavior.USE_CURRENT_DATE) {
                return DateUtil.setToBeginningOfDay(new Date());
            }
        }
        return this._startDate;
    }

    set startDate(value: string | Date) {
        this._startDate = this.normalizeDate(value);
    }

    get endDate(): Date {
        if (this._endDate == null) {
            if (this.nullEndDateBehavior === NullDateRangeDateBehavior.USE_OTHER_DATE
                && DateUtil.isDateValid(this._startDate)) {
                return DateUtil.setToEndOfDay(this._startDate);
            }
            if (this.nullEndDateBehavior === NullDateRangeDateBehavior.USE_CURRENT_DATE) {
                return DateUtil.setToEndOfDay(new Date());
            }
        }
        return this._endDate;
    }

    set endDate(value: string | Date) {
        this._endDate = this.normalizeDate(value);
        if (DateUtil.isDateValid(this._endDate)) {
            DateUtil.setToEndOfDay(this._endDate);
        }
    }

    get nullStartDateBehavior(): NullDateRangeDateBehavior {
        return this._nullStartDateBehavior ?? NullDateRangeDateBehavior.USE_OTHER_DATE;
    }

    set nullStartDateBehavior(value: NullDateRangeDateBehavior) {
        this._nullStartDateBehavior = value;
    }

    get nullEndDateBehavior(): NullDateRangeDateBehavior {
        return this._nullEndDateBehavior ?? NullDateRangeDateBehavior.USE_OTHER_DATE;
    }

    set nullEndDateBehavior(value: NullDateRangeDateBehavior) {
        this._nullEndDateBehavior = value;
    }

    hasValidDates(): boolean {
        try {
            this.validate();
        } catch (e) {
            return false;
        }
        return true;
    }

    /**
     * Validates the date range by checking both the start and end dates.
     *
     * @throws {Error} If the start date or end date is invalid, or if the start date is after the end date.
     */
    validate() {
        this.validateStartDate();
        this.validateEndDate();
        if (this.startDate != null && this.endDate != null) {
            if (this.startDate > this.endDate) {
                throw new Error("Start date must be before end date.");
            }
        }
    }

    private validateStartDate() {
        if (this.startDate == null && this.nullStartDateBehavior !== NullDateRangeDateBehavior.UNBOUND) {
            throw new Error("Start date is required.");
        }
        if (this.startDate != null && !DateUtil.isDateValid(this.startDate)) {
            throw new Error("Start date is invalid.");
        }
    }

    private validateEndDate() {
        if (this.endDate == null && this.nullEndDateBehavior !== NullDateRangeDateBehavior.UNBOUND) {
            throw new Error("End date is required.");
        }
        if (this.endDate != null && !DateUtil.isDateValid(this.endDate)) {
            throw new Error("End date is invalid.");
        }
    }

    public isSingleDay(): boolean {
        return this.endDate != null && DateUtil.dateDiff(DatePart.DAY, this.startDate, this.endDate) === 0;
    }

    getFormattedString(formatString: string = DateUtil.getUserDateFormat()): string {
        try {
            this.validate();
        } catch (e) {
            return null;
        }

        const startDateString = DateUtil.formatDate(this.startDate, formatString);
        const endDateString = DateUtil.formatDate(this.endDate, formatString);

        if (startDateString == null && endDateString == null) {
            return null;
        }

        if (startDateString == null) {
            return `Through ${endDateString}`;
        }

        if (endDateString == null) {
            return `Starting ${startDateString}`;
        }

        return `${startDateString} - ${endDateString}`;
    }

    public static parseDateRange(value: string | DateRange): DateRange {
        if (value instanceof DateRange) {
            return value;
        } else if (value == null) {
            return new DateRange();
        } else if (value.length < 10 || value.includes(",")) {
            // length < 10 assumes that a single MM/DD/YYYY date wasn't entered
            // users can enter expressions like : "t-1", "t-1,t" or "MM/DD/YYY, t"
            return DateRange.parseDateRangeWithKeywords(value);
        }
        return DateRange.parseFormattedDateRange(value);
    }

    public static parseFormattedDateRange(dateRangeString: string): DateRange {
        const dateRange = new DateRange();
        if (dateRangeString != null) {
            dateRangeString = dateRangeString.replace(/\s/g, '');
            if (dateRangeString.indexOf('-') > -1) {
                dateRange.startDate = DateUtil.parseFormattedDate(dateRangeString.split('-')[0]);
                dateRange.endDate = DateUtil.parseFormattedDate(dateRangeString.split('-')[1]);
            }
            else
                dateRange.startDate = DateUtil.parseFormattedDate(dateRangeString);
        }
        return dateRange;
    }

    public static parseDateRangeWithKeywords(dateRangeString: string): DateRange {
        const dateRange = new DateRange();

        if (dateRangeString) {
            const [beginningDateString, endDateString] = dateRangeString.replace(/\s/g, '').split(",");

            dateRange.startDate = DateUtil.parseDateWithKeywords(beginningDateString, true, false);

            if (endDateString) {
                dateRange.endDate = DateUtil.parseDateWithKeywords(endDateString, true, false);
            }
        }
        return dateRange;
    }

    public getDifferenceInDates(increment: DatePart = DatePart.DAY): number {
        return Math.abs(DateUtil.dateDiff(increment, this.startDate, this.endDate));
    }

    public static formatWithUnboundDates(startDate: string | Date, endDate: string | Date): string {
        return DateRange.createWithUnboundDates(startDate, endDate).getFormattedString()
    }

    public static createWithUnboundDates(startDate: string | Date, endDate: string | Date): DateRange {
        return new DateRange({
            nullStartDateBehavior: NullDateRangeDateBehavior.UNBOUND,
            nullEndDateBehavior: NullDateRangeDateBehavior.UNBOUND,
            startDate: startDate,
            endDate: endDate });
    }

    /**
     * Formats a date range string based on a start date and "days out".
     * If the start date is null, returns a range in the "Through [date]" format.
     *
     * @param startDate - The start date, either as a string or Date object.
     * @param daysOut - The number of days to add to the start date, either as a string or number.
     *
     * @returns A string representing the formatted date range, or null if both dates are invalid.
     */
    public static formatWithDaysOut(startDate: string | Date, daysOut: string | number): string {
       return DateRange.createWithDaysOut(startDate, daysOut).getFormattedString();
    }

    /**
     * Creates a DateRange object with a start date and an end date calculated by adding "daysOut" to the start date.
     * If the start date is not provided, the current date is used to calculate the end date.
     *
     * @param startDate - The start date, either as a string or Date object. If not provided, the current date is used.
     * @param daysOut - The number of days to add to the start date, either as a string or number.
     *
     * @returns A DateRange object containing the start date (either provided or defaulted) and the calculated end date.
     */
    public static createWithDaysOut(startDate?: string | Date, daysOut?: string | number): DateRange {
        const dateRange = new DateRange({
            startDate: startDate,
            nullEndDateBehavior: NullDateRangeDateBehavior.UNBOUND,
            nullStartDateBehavior: NullDateRangeDateBehavior.UNBOUND
        });
        const daysToAdd = (typeof daysOut === "string") ? parseInt(daysOut) : daysOut;
        if (Number.isInteger(daysToAdd)) {
            dateRange.endDate = DateUtil.dateAdd(DatePart.DAY, dateRange.startDate ?? new Date(), daysToAdd);
        }
        return dateRange;
    }
}
